import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { SearchPlanService } from '../../../services/searchPlan/searchPlanService.service';
import { Subject, takeUntil } from 'rxjs';
import { Router } from '@angular/router';
import { ISearchPlanReqBodyByContract, ISearchPlanReqBodyByPhone, ISearchPlanReqBodyByProperty } from '../../../interfaces/ISearchPlan';
import { FIND_PLAN_CONSTANTS, FIND_PLAN_ROUTER_CONSTANTS } from './find-plan.constants';
import { MessagesService } from '../../../services/messages/messages.service';
import { IMessage } from '../../../services/messages/messages.interface';
import { ISearchContractResponse } from '../../../interfaces/ISearchContract';
import { DataService } from '../../../services/data/data.service';
import { Ga4Service } from '../../../services/ga4/ga4.service';
import { ACC_LOGGEDOUT, ALL_PAGES, DASHBOARD_PAGE_LOCATION, DASHBOARD_PLAN_LOOKUP_HOUSE_NUMBER, DASHBOARD_PLAN_LOOKUP_MOBILE_NUMBER, DASHBOARD_PLAN_LOOKUP_PLAN_NUMBER, ERR_EVENT, REGISTRATION_FLOW_PLAN_LOOKUP } from '../../../services/ga4/ga4.constant';
import { trackByIndex } from '../../../utils/helper';

@Component({
  selector: 'app-find-plan',
  templateUrl: './find-plan.component.html',
  styleUrl: './find-plan.component.scss',
})
export class FindPlanComponent implements OnInit, OnDestroy {
  activeLayout: string = '';
  findPlanForm!: FormGroup;
  cleaner$ = new Subject<void>();
  findPlanConstants = FIND_PLAN_CONSTANTS;
  loading: boolean = false;
  auth0Userid: string = '';
  messagesAvailable: IMessage[] = [];
  trackByIndex = trackByIndex;
  constructor(private searchPlanService: SearchPlanService, private router: Router,
    private messages: MessagesService, private dataService: DataService, private gaService: Ga4Service) { }
  ngOnInit(): void {
    this.formInitialization();
    this.changeContainer(this.findPlanConstants.PHONE)
    this.auth0Userid = this.dataService.auth0UserId.getValue();

    this.dataService.messagesToDisplay$.pipe(takeUntil(this.cleaner$)).subscribe({
      next: (messages) => {
        this.messagesAvailable = messages;
      },
    });
  }

  /**
   * Method is used to initialize a form
   *
   * @memberof FindPlanComponent
   */
  formInitialization() {
    this.findPlanForm = new FormGroup({
      phoneNumber: new FormControl('', [Validators.minLength(10), Validators.maxLength(19), Validators.pattern(/^[0-9]*$/)]),
      zipCode: new FormControl('', [Validators.required, Validators.minLength(5), Validators.pattern(/^[0-9]*$/)]),
      lastName: new FormControl('', [Validators.minLength(2), Validators.maxLength(255)]),
      houseNumber: new FormControl('', [Validators.minLength(2)]),
      planNumber: new FormControl('', [Validators.maxLength(8), Validators.pattern(/^[a-zA-Z0-9]+$/)]),
    });
  }
  /**
   * Method is used to change the view in HTML
   * will set container value
   * @param {string} containerValue
   * @memberof FindPlanComponent
   */
  changeContainer(containerValue: string): void {
    this.findPlanForm.reset();
    this.activeLayout = containerValue;
    if (this.activeLayout === this.findPlanConstants.PROPERTY) {
      this.addValidators('lastName');
      this.addValidators('houseNumber');
      this.removeValidators('phoneNumber');
      this.removeValidators('planNumber');
    }
    if (this.activeLayout === this.findPlanConstants.CONTRACT) {
      this.addValidators('planNumber');
      this.removeValidators('lastName');
      this.removeValidators('phoneNumber')
      this.removeValidators('houseNumber');
    }
    if (this.activeLayout === this.findPlanConstants.PHONE) {
      this.addValidators('phoneNumber');
      this.removeValidators('planNumber');
      this.removeValidators('lastName');
      this.removeValidators('houseNumber');
    }
  }

  /**
   * Method is used to add validators to a passed form control name 
   *
   * @param {string} formControlName
   * @memberof FindPlanComponent
   */
  addValidators(formControlName: string): void {
    this.findPlanForm.controls[formControlName].addValidators([Validators.required])
    this.findPlanForm.controls[formControlName].updateValueAndValidity();
  }

  /**
   * Method is used to remove validators from a passed form control name 
   *
   * @param {string} formControlName
   * @memberof FindPlanComponent
   */
  removeValidators(formControlName: string): void {
    this.findPlanForm.controls[formControlName].removeValidators([Validators.required])
    this.findPlanForm.controls[formControlName].updateValueAndValidity();
  }

  /**
   * Method is used to get request body for API Call 
   *
   * @return {*}  {(ISearchPlanReqBodyByProperty | ISearchPlanReqBodyByPhone | ISearchPlanReqBodyByContract)}
   * @memberof FindPlanComponent
   */
  getRequestBody(): ISearchPlanReqBodyByProperty | ISearchPlanReqBodyByPhone | ISearchPlanReqBodyByContract {
    if (this.activeLayout === this.findPlanConstants.PROPERTY) {
      if (this.auth0Userid)
        this.gaService.buttonClickEvent(DASHBOARD_PAGE_LOCATION, DASHBOARD_PLAN_LOOKUP_HOUSE_NUMBER);
      else
        this.gaService.buttonClickEvent(REGISTRATION_FLOW_PLAN_LOOKUP, DASHBOARD_PLAN_LOOKUP_HOUSE_NUMBER);
      const reqParams: ISearchPlanReqBodyByProperty = {
        searchType: this.activeLayout,
        houseNumber: this.findPlanForm.controls['houseNumber'].value,
        zipCode: this.findPlanForm.controls['zipCode'].value,
        lastName: this.findPlanForm.controls['lastName'].value,
      }
      return reqParams;
    } else if (this.activeLayout === this.findPlanConstants.PHONE) {
      if (this.auth0Userid)
        this.gaService.buttonClickEvent(DASHBOARD_PAGE_LOCATION, DASHBOARD_PLAN_LOOKUP_MOBILE_NUMBER);
      else
        this.gaService.buttonClickEvent(REGISTRATION_FLOW_PLAN_LOOKUP, DASHBOARD_PLAN_LOOKUP_MOBILE_NUMBER);
      const reqParams: ISearchPlanReqBodyByPhone = {
        searchType: this.activeLayout,
        phoneNumber: this.findPlanForm.controls['phoneNumber'].value,
        zipCode: this.findPlanForm.controls['zipCode'].value
      }
      return reqParams;
    } else {
      if (this.auth0Userid)
        this.gaService.buttonClickEvent(DASHBOARD_PAGE_LOCATION, DASHBOARD_PLAN_LOOKUP_PLAN_NUMBER);
      else
        this.gaService.buttonClickEvent(REGISTRATION_FLOW_PLAN_LOOKUP, DASHBOARD_PLAN_LOOKUP_PLAN_NUMBER);
      const reqParams: ISearchPlanReqBodyByContract = {
        searchType: this.activeLayout,
        contractNumber: this.findPlanForm.controls['planNumber'].value,
        zipCode: this.findPlanForm.controls['zipCode'].value
      }
      return reqParams;
    }
  }

  /**
   * Method is used to find the contract based on 
   * phone/property/contract
   * @memberof FindPlanComponent
   */
  searchPlan(): void {
    this.loading = true;
    this.searchPlanService.findContract(this.getRequestBody())
      .pipe(takeUntil(this.cleaner$))
      .subscribe({
        next: (contractData: ISearchContractResponse[]) => {
          this.searchPlanService.plans$.next(contractData);
          if (this.auth0Userid)
            this.router.navigate([FIND_PLAN_ROUTER_CONSTANTS.PLAN_FOUND_AFTER_LOGIN])
          else
            this.router.navigate([FIND_PLAN_ROUTER_CONSTANTS.PLAN_FOUND])
          this.loading = false;
        },
        error: (err) => {
          this.loading = false;
          if (err) {
            const errorMessage: IMessage = {
              severity: 'error',
              text: err?.messages || this.findPlanConstants.FIND_PLAN_API_ERROR_MESSAGE,
              icon: 'pi pi-ban'
            };
            this.messages.addMessages(errorMessage);
            if (this.auth0Userid){
              this.gaService.messageEvent(ERR_EVENT, errorMessage.text);
            }else{
              this.gaService.messageEvent(ERR_EVENT, errorMessage.text, ALL_PAGES);
            }
            
          }
        }
      })
  }

  /**
   * Method will navigate the user to the login page 
   *
   * @memberof FindPlanComponent
   */
  navigateToLogIn(): void {
    this.router.navigate([this.auth0Userid ? FIND_PLAN_ROUTER_CONSTANTS.HOME : FIND_PLAN_ROUTER_CONSTANTS.LOGIN])
  }

  ngOnDestroy(): void {
    this.messages.clearMessages();
    this.cleaner$.next();
    this.cleaner$.complete();
  }
}
